/**
 * CategoryGroupModal.jsx
 *
 * Modal for creating or editing a Pathology Category Group.
 */

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Alert,
  Box,
  Stack,
  CircularProgress,
} from "@mui/material";

import {
  createCategoryGroup,
  updateCategoryGroup,
  selectCategoryGroupsLoading,
  selectCategoryGroupsError,
  clearError,
} from "../reducers/categoryGroupsSlice";

export default function CategoryGroupModal({ group, onClose }) {
  const dispatch = useDispatch();

  const access_token = useSelector((state) => state.authen.accessToken);
  const loading = useSelector(selectCategoryGroupsLoading);
  const apiError = useSelector(selectCategoryGroupsError);

  const isEdit = Boolean(group);

  // Form state
  const [groupName, setGroupName] = useState(group?.group_name ?? "");

  // Store aliases as textarea (newline-separated)
  const [aliasesText, setAliasesText] = useState(
    (group?.category_names ?? []).join("\n"),
  );

  const [validationErrors, setValidationErrors] = useState({});

  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();

    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Validation
  const validate = () => {
    const errs = {};

    if (!groupName.trim()) {
      errs.groupName = "Category Group name is required.";
    }

    const aliases = aliasesText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    if (!aliases.length) {
      errs.aliases = "At least one alias is required.";
    }

    setValidationErrors(errs);

    return Object.keys(errs).length === 0;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validate()) return;

    const category_names = aliasesText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      group_name: groupName.trim(),
      category_names,
    };

    let result;

    if (isEdit) {
      result = await dispatch(
        updateCategoryGroup({
          id: group.id,
          groupData: payload,
          access_token,
        }),
      );
    } else {
      result = await dispatch(
        createCategoryGroup({
          groupData: payload,
          access_token,
        }),
      );
    }

    if (!result.error) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const isBusy = loading.create || loading.update;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <DialogTitle>
        {isEdit ? `Edit: ${group.group_name}` : "New Category Group"}
      </DialogTitle>

      {/* Body */}
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* API Error */}
          {apiError && (
            <Alert severity="error">
              {typeof apiError === "string"
                ? apiError
                : JSON.stringify(apiError)}
            </Alert>
          )}

          {/* Group Name */}
          <Box>
            <TextField
              id="groupName"
              inputRef={nameRef}
              fullWidth
              label="Category Group Name *"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. C-Reactive Protein"
              error={Boolean(validationErrors.groupName)}
              helperText={validationErrors.groupName}
            />
          </Box>

          {/* Aliases */}
          <Box>
            <TextField
              id="aliases"
              fullWidth
              multiline
              minRows={10}
              label="Category Names / Aliases"
              value={aliasesText}
              onChange={(e) => setAliasesText(e.target.value)}
              placeholder={"CRP\nC REACTIVE PROTEIN\nPlasma C Reactive Protein"}
              error={Boolean(validationErrors.aliases)}
              helperText={
                validationErrors.aliases ||
                `${
                  aliasesText.split("\n").filter((s) => s.trim()).length
                } alias(es) entered`
              }
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              One alias per line
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit} disabled={isBusy}>
          {isBusy ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} color="inherit" />
              <span>Saving</span>
            </Stack>
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Category Group"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
